import React, { useState, useEffect } from 'react';
import { 
  Home, BookOpen, Mic, PiggyBank, Heart, 
  ChevronRight, ChevronLeft, ArrowLeft, Check, X, Plus,
  Play, Pause, RotateCcw, Target, TrendingUp, AlertCircle,
  Calendar, Clock, Volume2, Edit3, Trash2, Save, Settings,
  HelpCircle, ExternalLink, Link
} from 'lucide-react';
import './App.css';

// ============================================
// REBUILD PORT CONFIGURATION
// ============================================

// ============================================
// REBUILD PORT CONFIGURATION
// ============================================

const REBUILD_API_URL = 'https://us-central1-nm-rebuild.cloudfunctions.net/logActivity';
const REBUILD_HOME = 'https://rebuild.navigationalmind.com/clinician';

// Store patientId for return navigation
let rebuildPatientId = null;

// Parse rebuild token from URL
const parseRebuildToken = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('rebuild_token');
    if (token) {
      const decoded = JSON.parse(atob(token));
      rebuildPatientId = decoded.patientId;
      return {
        token,
        patientId: decoded.patientId,
        tool: decoded.tool,
        timestamp: decoded.timestamp
      };
    }
  } catch (e) {
    console.error('Error parsing rebuild token:', e);
  }
  return null;
};

// Report activity to Rebuild
const reportToRebuild = async (rebuildContext, event, data) => {
  if (!rebuildContext) return;
  
  const payload = {
    patientId: rebuildContext.patientId,
    tool: 'swatd',
    event,
    data
  };
  
  console.log('Rebuild Activity:', payload);
  
  try {
    await fetch(REBUILD_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  } catch (e) {
    console.error('Error reporting to Rebuild:', e);
  }
};

// Return to Rebuild with patient context
const returnToRebuild = () => {
  if (rebuildPatientId) {
    localStorage.setItem('returnToPatient', rebuildPatientId);
  }
  window.location.href = REBUILD_HOME;
};

// ============================================
// LOCAL STORAGE HELPERS
// ============================================

const STORAGE_KEYS = {
  learnCompleted: 'swatd_learn_completed',
  statements: 'swatd_statements',
  practiceLog: 'swatd_practice_log',
  bankEntries: 'swatd_bank_entries',
  bfChecks: 'swatd_bf_checks',
  rebuildPort: 'swatd_rebuild_port' // Future: Rebuild connection data
};

const loadFromStorage = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('Storage error:', e);
  }
};

// ============================================
// MAIN APP COMPONENT
// ============================================

function App() {
  // Navigation
  const [view, setView] = useState('home');
  const [learnStep, setLearnStep] = useState(0);
  
  // Rebuild Port context
  const [rebuildContext, setRebuildContext] = useState(null);
  
  // Detect Rebuild token on mount
  useEffect(() => {
    const context = parseRebuildToken();
    if (context) {
      setRebuildContext(context);
      console.log('Connected to Rebuild:', context);
    }
  }, []);
  
  // Learn module state
  const [learnCompleted, setLearnCompleted] = useState(() => 
    loadFromStorage(STORAGE_KEYS.learnCompleted, false)
  );
  
  // Articulation module state
  const [statements, setStatements] = useState(() => 
    loadFromStorage(STORAGE_KEYS.statements, [])
  );
  const [practiceLog, setPracticeLog] = useState(() => 
    loadFromStorage(STORAGE_KEYS.practiceLog, [])
  );
  const [artStep, setArtStep] = useState(0);
  const [presentWords, setPresentWords] = useState(['', '', '']);
  const [desiredWords, setDesiredWords] = useState(['', '', '']);
  const [fullStatement, setFullStatement] = useState('');
  const [confidence, setConfidence] = useState(50);
  const [conviction, setConviction] = useState(50);
  const [editingStatement, setEditingStatement] = useState(null);
  const [practicingStatement, setPracticingStatement] = useState(null);
  const [practiceCount, setPracticeCount] = useState(0);
  const [practiceSetCount, setPracticeSetCount] = useState(0);
  
  // Validation Bank state
  const [bankEntries, setBankEntries] = useState(() => 
    loadFromStorage(STORAGE_KEYS.bankEntries, [])
  );
  const [cantText, setCantText] = useState('');
  const [cantDistress, setCantDistress] = useState('mild');
  const [canDoTexts, setCanDoTexts] = useState(['']);
  const [bankView, setBankView] = useState('main');
  
  // Best Friend state
  const [bfChecks, setBfChecks] = useState(() => 
    loadFromStorage(STORAGE_KEYS.bfChecks, [])
  );
  const [bfStep, setBfStep] = useState(0);
  const [bfSituation, setBfSituation] = useState('');
  const [bfFeeling, setBfFeeling] = useState('');
  const [bfAction, setBfAction] = useState('');
  const [bfHasGap, setBfHasGap] = useState(null);
  const [bfBestAction, setBfBestAction] = useState('');
  const [bfCommit, setBfCommit] = useState(false);
  
  // Persist data on changes
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.learnCompleted, learnCompleted);
  }, [learnCompleted]);
  
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.statements, statements);
  }, [statements]);
  
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.practiceLog, practiceLog);
  }, [practiceLog]);
  
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.bankEntries, bankEntries);
  }, [bankEntries]);
  
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.bfChecks, bfChecks);
  }, [bfChecks]);

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  
  const getRequiredRatio = (distress) => {
    switch (distress) {
      case 'mild': return 2;
      case 'moderate': return 3;
      case 'hard': return 5;
      case 'severe': return 10;
      default: return 2;
    }
  };
  
  const getDistressLabel = (distress) => {
    switch (distress) {
      case 'mild': return 'Mild (2:1 ratio)';
      case 'moderate': return 'Moderate (3:1 ratio)';
      case 'hard': return 'Hard (5:1 ratio)';
      case 'severe': return 'Severe (10:1 ratio)';
      default: return 'Mild';
    }
  };
  
  const getTodayKey = () => new Date().toISOString().split('T')[0];
  
  const getTodayPractice = (statementId) => {
    const today = getTodayKey();
    return practiceLog.find(p => p.statementId === statementId && p.date === today);
  };
  
  const resetArticulationForm = () => {
    setArtStep(0);
    setPresentWords(['', '', '']);
    setDesiredWords(['', '', '']);
    setFullStatement('');
    setConfidence(50);
    setConviction(50);
    setEditingStatement(null);
  };
  
  const resetBfForm = () => {
    setBfStep(0);
    setBfSituation('');
    setBfFeeling('');
    setBfAction('');
    setBfHasGap(null);
    setBfBestAction('');
    setBfCommit(false);
  };

  // ============================================
  // HOME VIEW
  // ============================================
  
  const renderHome = () => (
    <div className="home-view">
      {rebuildContext && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '12px', 
          marginBottom: '16px' 
        }}>
          <div className="rebuild-badge">
            <Link size={16} />
            <span>Connected to Rebuild</span>
          </div>
          <button 
            onClick={returnToRebuild}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: 'rgba(100, 181, 246, 0.2)',
              border: '1px solid rgba(100, 181, 246, 0.5)',
              borderRadius: '20px',
              fontSize: '14px',
              color: '#64b5f6',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            ← Return to Rebuild
          </button>
        </div>
      )}
      
      <header className="app-header">
        <div className="logo-container">
          <h1 className="app-title">SWATD</h1>
          <p className="app-subtitle">Spoken Word + Action + Thought = Direction</p>
        </div>
      </header>
      
      <div className="modules-grid">
        <button 
          className={`module-card learn ${learnCompleted ? 'completed' : 'primary'}`}
          onClick={() => setView('learn')}
        >
          <div className="module-icon">
            <BookOpen size={32} />
          </div>
          <div className="module-content">
            <h2>Learn</h2>
            <p>Understand the SW+A+T=D concept</p>
            {learnCompleted && <span className="badge">✓ Completed</span>}
          </div>
          <ChevronRight className="chevron" size={24} />
        </button>
        
        <button 
          className="module-card articulation"
          onClick={() => setView('articulation')}
        >
          <div className="module-icon">
            <Mic size={32} />
          </div>
          <div className="module-content">
            <h2>Articulation</h2>
            <p>Build your "I WILL..." statements</p>
            {statements.length > 0 && (
              <span className="badge">{statements.length} statement{statements.length > 1 ? 's' : ''}</span>
            )}
          </div>
          <ChevronRight className="chevron" size={24} />
        </button>
        
        <button 
          className="module-card bank"
          onClick={() => setView('bank')}
        >
          <div className="module-icon">
            <PiggyBank size={32} />
          </div>
          <div className="module-content">
            <h2>Validation Bank</h2>
            <p>Build your "Can Do" ratio</p>
            {bankEntries.length > 0 && (
              <span className="badge">{bankEntries.length} entr{bankEntries.length > 1 ? 'ies' : 'y'}</span>
            )}
          </div>
          <ChevronRight className="chevron" size={24} />
        </button>
        
        <button 
          className="module-card bestfriend"
          onClick={() => setView('bestfriend')}
        >
          <div className="module-icon">
            <Heart size={32} />
          </div>
          <div className="module-content">
            <h2>Best Friend</h2>
            <p>Am I being my own best friend?</p>
            {bfChecks.length > 0 && (
              <span className="badge">{bfChecks.length} check{bfChecks.length > 1 ? 's' : ''}</span>
            )}
          </div>
          <ChevronRight className="chevron" size={24} />
        </button>
      </div>
      
      <footer className="app-footer">
        <p className="footer-text">Part of the Navigational Mind Framework</p>
        <p className="footer-link">
          <a href="https://navigationalmind.com" target="_blank" rel="noopener noreferrer">
            navigationalmind.com <ExternalLink size={12} />
          </a>
        </p>
      </footer>
    </div>
  );

  // ============================================
  // LEARN MODULE
  // ============================================
  
  const learnSlides = [
    {
      title: "The Mind as Liquid, Solid, and Gas",
      content: (
        <>
          <p>Your mind operates in three states:</p>
          <div className="concept-grid">
            <div className="concept-card">
              <h4>💧 Liquid</h4>
              <p><strong>Thoughts</strong> — flowing, changing, adaptable</p>
            </div>
            <div className="concept-card">
              <h4>🪨 Solid</h4>
              <p><strong>Actions</strong> — concrete, visible, measurable</p>
            </div>
            <div className="concept-card">
              <h4>💨 Gas</h4>
              <p><strong>Words</strong> — expandable, filling available space</p>
            </div>
          </div>
          <p className="insight">Each state has different properties. Direction requires all three working together.</p>
        </>
      )
    },
    {
      title: "The Problem: States in Isolation",
      content: (
        <>
          <p>When these states don't connect, navigation breaks down:</p>
          <ul className="problem-list">
            <li><strong>Thoughts alone</strong> → rumination, paralysis</li>
            <li><strong>Actions alone</strong> → impulsivity, disconnection</li>
            <li><strong>Words alone</strong> → empty promises, wishful thinking</li>
          </ul>
          <p className="insight">You've probably experienced each of these. They all feel stuck in different ways.</p>
        </>
      )
    },
    {
      title: "The Formula: SW + A + T = D",
      content: (
        <>
          <div className="formula-display">
            <span className="formula-part">SW</span>
            <span className="formula-op">+</span>
            <span className="formula-part">A</span>
            <span className="formula-op">+</span>
            <span className="formula-part">T</span>
            <span className="formula-op">=</span>
            <span className="formula-result">D</span>
          </div>
          <div className="formula-legend">
            <p><strong>Spoken Word</strong> + <strong>Action</strong> + <strong>Thought</strong> = <strong>Direction</strong></p>
          </div>
          <p className="insight">When you speak your direction aloud, connect it to action, and align your thoughts — you create forward movement.</p>
        </>
      )
    },
    {
      title: "Why Spoken Word First",
      content: (
        <>
          <p>The formula starts with <strong>Spoken Word</strong> deliberately:</p>
          <ul className="benefit-list">
            <li>Speaking forces specificity — vague thoughts become concrete words</li>
            <li>Your brain hears you say it — different processing than thinking</li>
            <li>Words expand into space — they become real outside your head</li>
            <li>Commitment increases — what you say, you're more likely to do</li>
          </ul>
          <p className="insight">This is why articulation — building and speaking your statements — is the foundation.</p>
        </>
      )
    },
    {
      title: "The Articulation Statement",
      content: (
        <>
          <p>An articulation statement follows this form:</p>
          <div className="statement-template">
            <span className="statement-fixed">I WILL BE</span>
            <span className="statement-variable">[action words in your desired direction]</span>
          </div>
          <p>For example:</p>
          <ul className="example-list">
            <li>"I WILL BE present, calm, and grounded"</li>
            <li>"I WILL BE patient, curious, and open"</li>
            <li>"I WILL BE focused, disciplined, and steady"</li>
          </ul>
          <p className="insight">The statement describes who you're becoming through your actions — not what you're avoiding.</p>
        </>
      )
    },
    {
      title: "The 10×10 Practice",
      content: (
        <>
          <p>Once you have your statement, the practice is simple:</p>
          <div className="practice-display">
            <div className="practice-number">10</div>
            <div className="practice-times">times, spoken aloud</div>
            <div className="practice-number">10</div>
            <div className="practice-times">times per day</div>
          </div>
          <p>That's 100 repetitions daily. Each time:</p>
          <ul className="practice-steps">
            <li>Speak the full statement out loud</li>
            <li>Let your brain hear the words</li>
            <li>Feel the direction in your body</li>
          </ul>
          <p className="insight">Repetition builds the neural pathway. Speaking makes it real.</p>
        </>
      )
    },
    {
      title: "Confidence and Conviction",
      content: (
        <>
          <p>A good articulation statement needs both:</p>
          <div className="cc-grid">
            <div className="cc-card">
              <h4>🎯 Confidence</h4>
              <p>"I believe this is the right direction"</p>
              <p className="cc-target">Target: 80%+</p>
            </div>
            <div className="cc-card">
              <h4>💪 Conviction</h4>
              <p>"I believe I can move in this direction"</p>
              <p className="cc-target">Target: 80%+</p>
            </div>
          </div>
          <p className="insight">If either is below 80%, adjust your statement until it resonates. Your statement should feel true and achievable.</p>
        </>
      )
    },
    {
      title: "You're Ready",
      content: (
        <>
          <p>You now understand SW+A+T=D:</p>
          <div className="summary-list">
            <div className="summary-item">✓ Mind states: Liquid (thought), Solid (action), Gas (word)</div>
            <div className="summary-item">✓ Formula: Spoken Word + Action + Thought = Direction</div>
            <div className="summary-item">✓ Tool: "I WILL BE..." articulation statements</div>
            <div className="summary-item">✓ Practice: 10 times, spoken aloud, 10 times daily</div>
            <div className="summary-item">✓ Check: Confidence and Conviction both at 80%+</div>
          </div>
          <p className="insight">The Articulation module will help you build your first statement.</p>
        </>
      )
    }
  ];
  
  const renderLearn = () => (
    <div className="learn-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('home')}>
          <ArrowLeft size={20} />
        </button>
        <h1>Learn SW+A+T=D</h1>
      </div>
      
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${((learnStep + 1) / learnSlides.length) * 100}%` }}
        />
      </div>
      
      <div className="slide-container">
        <div className="slide">
          <h2 className="slide-title">{learnSlides[learnStep].title}</h2>
          <div className="slide-content">
            {learnSlides[learnStep].content}
          </div>
        </div>
      </div>
      
      <div className="slide-nav">
        <button 
          className="nav-btn secondary"
          onClick={() => setLearnStep(learnStep - 1)}
          disabled={learnStep === 0}
        >
          <ChevronLeft size={20} /> Previous
        </button>
        
        <span className="slide-count">{learnStep + 1} / {learnSlides.length}</span>
        
        {learnStep < learnSlides.length - 1 ? (
          <button 
            className="nav-btn primary"
            onClick={() => setLearnStep(learnStep + 1)}
          >
            Next <ChevronRight size={20} />
          </button>
        ) : (
          <button 
            className="nav-btn primary complete"
            onClick={() => {
              setLearnCompleted(true);
              reportToRebuild(rebuildContext, 'learn_completed', {});
              setView('home');
            }}
          >
            <Check size={20} /> Complete
          </button>
        )}
      </div>
    </div>
  );

  // ============================================
  // ARTICULATION MODULE
  // ============================================
  
  const renderArticulation = () => (
    <div className="articulation-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('home')}>
          <ArrowLeft size={20} />
        </button>
        <h1>Articulation</h1>
      </div>
      
      <div className="art-menu">
        <button 
          className="art-menu-btn primary"
          onClick={() => {
            resetArticulationForm();
            setView('art-create');
          }}
        >
          <Plus size={24} />
          <span>Create New Statement</span>
        </button>
        
        {statements.length > 0 && (
          <button 
            className="art-menu-btn"
            onClick={() => setView('art-saved')}
          >
            <Target size={24} />
            <span>My Statements ({statements.length})</span>
          </button>
        )}
        
        {statements.length > 0 && (
          <button 
            className="art-menu-btn"
            onClick={() => setView('art-practice')}
          >
            <Volume2 size={24} />
            <span>Daily Practice</span>
          </button>
        )}
      </div>
      
      {!learnCompleted && (
        <div className="guidance-card">
          <HelpCircle size={20} />
          <p>Haven't completed the Learn module yet? Understanding SW+A+T=D first will help you create better statements.</p>
          <button onClick={() => setView('learn')}>Go to Learn</button>
        </div>
      )}
    </div>
  );
  
  const renderArtCreate = () => {
    const steps = [
      {
        title: "Step 1: Current Direction",
        subtitle: "What action words describe where you are NOW?",
        content: (
          <div className="word-inputs">
            <p className="instruction">Enter 2-3 words that describe how you're currently acting or being:</p>
            {presentWords.map((word, i) => (
              <input
                key={i}
                type="text"
                placeholder={`Word ${i + 1}${i === 2 ? ' (optional)' : ''}`}
                value={word}
                onChange={(e) => {
                  const newWords = [...presentWords];
                  newWords[i] = e.target.value;
                  setPresentWords(newWords);
                }}
              />
            ))}
            <p className="example">Examples: anxious, scattered, withdrawn, reactive, stuck</p>
          </div>
        ),
        canContinue: presentWords.filter(w => w.trim()).length >= 2
      },
      {
        title: "Step 2: Desired Direction",
        subtitle: "What action words describe where you WANT to be?",
        content: (
          <div className="word-inputs">
            <p className="instruction">Replace those words with your desired direction:</p>
            <div className="word-comparison">
              {presentWords.filter(w => w.trim()).map((word, i) => (
                <div key={i} className="word-pair">
                  <span className="from-word">{word}</span>
                  <span className="arrow">→</span>
                  <input
                    type="text"
                    placeholder="becomes..."
                    value={desiredWords[i]}
                    onChange={(e) => {
                      const newWords = [...desiredWords];
                      newWords[i] = e.target.value;
                      setDesiredWords(newWords);
                    }}
                  />
                </div>
              ))}
            </div>
            <p className="example">Examples: calm, focused, connected, responsive, moving</p>
          </div>
        ),
        canContinue: desiredWords.filter((w, i) => w.trim() && presentWords[i]?.trim()).length >= 2
      },
      {
        title: "Step 3: Form Your Statement",
        subtitle: "Build your 'I WILL BE...' statement",
        content: (
          <div className="statement-builder">
            <p className="instruction">Your desired words: <strong>{desiredWords.filter(w => w.trim()).join(', ')}</strong></p>
            <div className="statement-input-group">
              <span className="statement-prefix">I WILL BE</span>
              <textarea
                placeholder="present, calm, and grounded"
                value={fullStatement}
                onChange={(e) => setFullStatement(e.target.value)}
                rows={2}
              />
            </div>
            <p className="tip">Use your desired words or expand on them. The statement should feel natural to say.</p>
            {fullStatement && (
              <div className="statement-preview">
                <h4>Your Statement:</h4>
                <p className="preview-text">"I WILL BE {fullStatement}"</p>
              </div>
            )}
          </div>
        ),
        canContinue: fullStatement.trim().length > 5
      },
      {
        title: "Step 4: Check Resonance",
        subtitle: "Does this statement feel right?",
        content: (
          <div className="resonance-check">
            <div className="statement-display">
              <p>"I WILL BE {fullStatement}"</p>
            </div>
            
            <div className="slider-group">
              <label>
                <span className="slider-label">Confidence</span>
                <span className="slider-hint">"I believe this is the right direction"</span>
              </label>
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={confidence}
                onChange={(e) => setConfidence(Number(e.target.value))}
              />
              <span className={`slider-value ${confidence >= 80 ? 'good' : 'low'}`}>
                {confidence}%
              </span>
            </div>
            
            <div className="slider-group">
              <label>
                <span className="slider-label">Conviction</span>
                <span className="slider-hint">"I believe I can move in this direction"</span>
              </label>
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={conviction}
                onChange={(e) => setConviction(Number(e.target.value))}
              />
              <span className={`slider-value ${conviction >= 80 ? 'good' : 'low'}`}>
                {conviction}%
              </span>
            </div>
            
            {(confidence < 80 || conviction < 80) && (
              <div className="resonance-warning">
                <AlertCircle size={18} />
                <p>Both should be 80%+ for an effective statement. Consider adjusting the words until it feels true and achievable.</p>
              </div>
            )}
          </div>
        ),
        canContinue: confidence >= 80 && conviction >= 80
      }
    ];
    
    const saveStatement = () => {
      const newStatement = {
        id: editingStatement?.id || Date.now(),
        presentWords: presentWords.filter(w => w.trim()),
        desiredWords: desiredWords.filter(w => w.trim()),
        statement: fullStatement,
        confidence,
        conviction,
        createdAt: editingStatement?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      if (editingStatement) {
        setStatements(statements.map(s => s.id === editingStatement.id ? newStatement : s));
        reportToRebuild(rebuildContext, 'statement_edited', {
          statement_id: newStatement.id,
          statement_text: newStatement.statement,
          confidence: newStatement.confidence,
          conviction: newStatement.conviction
        });
      } else {
        setStatements([...statements, newStatement]);
        reportToRebuild(rebuildContext, 'statement_created', {
          statement_id: newStatement.id,
          statement_text: newStatement.statement,
          confidence: newStatement.confidence,
          conviction: newStatement.conviction
        });
      }
      
      resetArticulationForm();
      setView('art-saved');
    };
    
    return (
      <div className="art-create-view">
        <div className="view-header">
          <button className="back-btn" onClick={() => {
            resetArticulationForm();
            setView('articulation');
          }}>
            <ArrowLeft size={20} />
          </button>
          <h1>{editingStatement ? 'Edit' : 'Create'} Statement</h1>
        </div>
        
        <div className="step-progress">
          {steps.map((_, i) => (
            <div 
              key={i} 
              className={`step-dot ${i === artStep ? 'active' : ''} ${i < artStep ? 'done' : ''}`}
              onClick={() => i < artStep && setArtStep(i)}
            />
          ))}
        </div>
        
        <div className="step-card">
          <h2>{steps[artStep].title}</h2>
          <p className="step-subtitle">{steps[artStep].subtitle}</p>
          {steps[artStep].content}
        </div>
        
        <div className="step-nav">
          {artStep > 0 && (
            <button className="nav-btn secondary" onClick={() => setArtStep(artStep - 1)}>
              <ChevronLeft size={18} /> Back
            </button>
          )}
          
          {artStep < steps.length - 1 ? (
            <button 
              className="nav-btn primary"
              onClick={() => setArtStep(artStep + 1)}
              disabled={!steps[artStep].canContinue}
            >
              Continue <ChevronRight size={18} />
            </button>
          ) : (
            <button 
              className="nav-btn primary save"
              onClick={saveStatement}
              disabled={!steps[artStep].canContinue}
            >
              <Save size={18} /> Save Statement
            </button>
          )}
        </div>
      </div>
    );
  };
  
  const renderArtSaved = () => (
    <div className="art-saved-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('articulation')}>
          <ArrowLeft size={20} />
        </button>
        <h1>My Statements</h1>
      </div>
      
      <div className="statements-list">
        {statements.length === 0 ? (
          <div className="empty-state">
            <p>No statements yet.</p>
            <button className="nav-btn primary" onClick={() => {
              resetArticulationForm();
              setView('art-create');
            }}>
              <Plus size={18} /> Create First Statement
            </button>
          </div>
        ) : (
          statements.map(stmt => {
            const todayPractice = getTodayPractice(stmt.id);
            return (
              <div key={stmt.id} className="statement-card">
                <div className="statement-text">
                  "I WILL BE {stmt.statement}"
                </div>
                <div className="statement-meta">
                  <span className="meta-item">
                    <Target size={14} /> {stmt.confidence}% conf / {stmt.conviction}% conv
                  </span>
                  {todayPractice && (
                    <span className="meta-item practiced">
                      <Check size={14} /> {todayPractice.sets}/10 sets today
                    </span>
                  )}
                </div>
                <div className="statement-actions">
                  <button onClick={() => {
                    setPracticingStatement(stmt);
                    setPracticeCount(0);
                    setPracticeSetCount(todayPractice?.sets || 0);
                    setView('art-practice-session');
                  }}>
                    <Volume2 size={16} /> Practice
                  </button>
                  <button onClick={() => {
                    setEditingStatement(stmt);
                    setPresentWords([...stmt.presentWords, '', ''].slice(0, 3));
                    setDesiredWords([...stmt.desiredWords, '', ''].slice(0, 3));
                    setFullStatement(stmt.statement);
                    setConfidence(stmt.confidence);
                    setConviction(stmt.conviction);
                    setArtStep(0);
                    setView('art-create');
                  }}>
                    <Edit3 size={16} /> Edit
                  </button>
                  <button className="danger" onClick={() => {
                    if (window.confirm('Delete this statement?')) {
                      setStatements(statements.filter(s => s.id !== stmt.id));
                    }
                  }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
  
  const renderArtPractice = () => {
    const today = getTodayKey();
    
    return (
      <div className="art-practice-view">
        <div className="view-header">
          <button className="back-btn" onClick={() => setView('articulation')}>
            <ArrowLeft size={20} />
          </button>
          <h1>Daily Practice</h1>
        </div>
        
        <div className="practice-date">
          <Calendar size={18} />
          <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
        </div>
        
        <div className="practice-list">
          {statements.map(stmt => {
            const todayPractice = getTodayPractice(stmt.id);
            const sets = todayPractice?.sets || 0;
            const isComplete = sets >= 10;
            
            return (
              <div key={stmt.id} className={`practice-card ${isComplete ? 'complete' : ''}`}>
                <div className="practice-statement">
                  "I WILL BE {stmt.statement}"
                </div>
                <div className="practice-progress">
                  <div className="progress-bar-small">
                    <div className="progress-fill" style={{ width: `${(sets / 10) * 100}%` }} />
                  </div>
                  <span>{sets}/10 sets</span>
                </div>
                <button 
                  className={`practice-btn ${isComplete ? 'complete' : ''}`}
                  onClick={() => {
                    setPracticingStatement(stmt);
                    setPracticeCount(0);
                    setPracticeSetCount(sets);
                    setView('art-practice-session');
                  }}
                >
                  {isComplete ? (
                    <><Check size={18} /> Done for today</>
                  ) : (
                    <><Volume2 size={18} /> Practice now</>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  const renderArtPracticeSession = () => {
    if (!practicingStatement) return null;
    
    const completeSet = () => {
      const today = getTodayKey();
      const existingLog = practiceLog.find(
        p => p.statementId === practicingStatement.id && p.date === today
      );
      
      const newSetCount = practiceSetCount + 1;
      
      if (existingLog) {
        setPracticeLog(practiceLog.map(p => 
          p.statementId === practicingStatement.id && p.date === today
            ? { ...p, sets: p.sets + 1, reps: p.reps + 10 }
            : p
        ));
      } else {
        setPracticeLog([...practiceLog, {
          statementId: practicingStatement.id,
          date: today,
          sets: 1,
          reps: 10
        }]);
      }
      
      reportToRebuild(rebuildContext, 'practice_completed', {
        statement_id: practicingStatement.id,
        sets_completed: 1,
        total_sets_today: newSetCount
      });
      
      setPracticeSetCount(newSetCount);
      setPracticeCount(0);
    };
    
    return (
      <div className="practice-session-view">
        <div className="view-header">
          <button className="back-btn" onClick={() => {
            setPracticingStatement(null);
            setView('art-practice');
          }}>
            <ArrowLeft size={20} />
          </button>
          <h1>Practice Session</h1>
        </div>
        
        <div className="session-content">
          <div className="session-statement">
            <p className="label">Say this out loud:</p>
            <p className="statement">"I WILL BE {practicingStatement.statement}"</p>
          </div>
          
          <div className="rep-counter">
            <div className="counter-display">{practiceCount}/10</div>
            <p>repetitions in this set</p>
          </div>
          
          <div className="rep-dots">
            {[...Array(10)].map((_, i) => (
              <div 
                key={i} 
                className={`rep-dot ${i < practiceCount ? 'done' : ''}`}
              />
            ))}
          </div>
          
          {practiceCount < 10 ? (
            <button 
              className="tap-btn"
              onClick={() => setPracticeCount(practiceCount + 1)}
            >
              <Volume2 size={32} />
              <span>Tap after saying it</span>
            </button>
          ) : (
            <button 
              className="complete-set-btn"
              onClick={completeSet}
            >
              <Check size={24} />
              <span>Complete Set {practiceSetCount + 1}/10</span>
            </button>
          )}
          
          <div className="session-progress">
            <span>Today's progress: {practiceSetCount}/10 sets</span>
            <div className="progress-bar-small">
              <div className="progress-fill" style={{ width: `${(practiceSetCount / 10) * 100}%` }} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // VALIDATION BANK MODULE
  // ============================================
  
  const renderBank = () => (
    <div className="bank-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('home')}>
          <ArrowLeft size={20} />
        </button>
        <h1>Validation Bank</h1>
      </div>
      
      <div className="bank-concept">
        <div className="bank-metaphor">
          <div className="metaphor-item">
            <span className="role">You are the BANK</span>
            <span className="meaning">where your sense of value resides</span>
          </div>
          <div className="metaphor-item">
            <span className="role">You are the BANKER</span>
            <span className="meaning">only you can make deposits</span>
          </div>
          <div className="metaphor-item">
            <span className="role">You are the CLIENT</span>
            <span className="meaning">only you can withdraw</span>
          </div>
        </div>
      </div>
      
      <div className="bank-menu">
        <button 
          className="bank-menu-btn primary"
          onClick={() => {
            setCantText('');
            setCantDistress('mild');
            setCanDoTexts(['']);
            setBankView('add-cant');
          }}
        >
          <Plus size={24} />
          <span>Acknowledge a Can't</span>
          <p className="btn-hint">Start here when something is hard</p>
        </button>
        
        <button 
          className="bank-menu-btn"
          onClick={() => setBankView('history')}
        >
          <TrendingUp size={24} />
          <span>View History</span>
          {bankEntries.length > 0 && <span className="count">{bankEntries.length}</span>}
        </button>
      </div>
    </div>
  );
  
  const renderBankAddCant = () => {
    const requiredRatio = getRequiredRatio(cantDistress);
    const currentCanDos = canDoTexts.filter(t => t.trim()).length;
    const ratioMet = currentCanDos >= requiredRatio;
    
    return (
      <div className="bank-add-view">
        <div className="view-header">
          <button className="back-btn" onClick={() => setBankView('main')}>
            <ArrowLeft size={20} />
          </button>
          <h1>Acknowledge a Can't</h1>
        </div>
        
        <div className="bank-form">
          <div className="form-section">
            <label>What's the "Can't" right now?</label>
            <p className="hint">It's okay to name it — this is not denial</p>
            <textarea
              placeholder="I am financially broke"
              value={cantText}
              onChange={(e) => setCantText(e.target.value)}
              rows={2}
            />
          </div>
          
          <div className="form-section">
            <label>How distressing is this?</label>
            <div className="distress-options">
              {['mild', 'moderate', 'hard', 'severe'].map(level => (
                <button
                  key={level}
                  className={`distress-btn ${cantDistress === level ? 'active' : ''}`}
                  onClick={() => setCantDistress(level)}
                >
                  {getDistressLabel(level)}
                </button>
              ))}
            </div>
          </div>
          
          {cantText.trim() && (
            <div className="form-section can-do-section">
              <label>Now list what you CAN do</label>
              <p className="hint">
                For {cantDistress} distress, aim for {requiredRatio}:1 ratio 
                ({currentCanDos}/{requiredRatio} so far)
              </p>
              
              <div className="ratio-progress">
                <div 
                  className="ratio-fill" 
                  style={{ width: `${Math.min((currentCanDos / requiredRatio) * 100, 100)}%` }}
                />
              </div>
              
              {canDoTexts.map((text, i) => (
                <div key={i} className="can-do-input-row">
                  <span className="can-do-number">{i + 1}.</span>
                  <input
                    type="text"
                    placeholder="Something you CAN do or that IS working"
                    value={text}
                    onChange={(e) => {
                      const newTexts = [...canDoTexts];
                      newTexts[i] = e.target.value;
                      setCanDoTexts(newTexts);
                    }}
                  />
                </div>
              ))}
              
              <button 
                className="add-can-do-btn"
                onClick={() => setCanDoTexts([...canDoTexts, ''])}
              >
                <Plus size={16} /> Add another Can Do
              </button>
            </div>
          )}
          
          <div className="form-actions">
            <button 
              className="nav-btn primary"
              disabled={!cantText.trim() || !ratioMet}
              onClick={() => {
                const entry = {
                  id: Date.now(),
                  cant: cantText,
                  distress: cantDistress,
                  canDos: canDoTexts.filter(t => t.trim()),
                  requiredRatio,
                  achievedRatio: currentCanDos,
                  timestamp: new Date().toISOString()
                };
                setBankEntries([entry, ...bankEntries]);
                reportToRebuild(rebuildContext, 'bank_deposit', {
                  entry_id: entry.id,
                  cant_text: entry.cant,
                  distress_level: entry.distress,
                  can_do_count: entry.canDos.length,
                  ratio_achieved: entry.achievedRatio
                });
                setBankView('complete');
              }}
            >
              {ratioMet ? (
                <><Check size={18} /> Make Deposit</>
              ) : (
                <>Need {requiredRatio - currentCanDos} more Can Do{requiredRatio - currentCanDos > 1 ? 's' : ''}</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  const renderBankComplete = () => (
    <div className="bank-complete-view">
      <div className="success-card">
        <div className="success-icon">
          <PiggyBank size={48} />
        </div>
        <h2>Deposit Made!</h2>
        <p>You acknowledged what's hard AND reminded yourself of what's working.</p>
        <p className="insight">This is not denial. This is balance.</p>
        <button className="nav-btn primary" onClick={() => {
          setBankView('main');
        }}>
          <Home size={18} /> Done
        </button>
      </div>
    </div>
  );
  
  const renderBankHistory = () => (
    <div className="bank-history-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setBankView('main')}>
          <ArrowLeft size={20} />
        </button>
        <h1>Bank History</h1>
      </div>
      
      <div className="history-list">
        {bankEntries.length === 0 ? (
          <div className="empty-state">
            <p>No entries yet.</p>
          </div>
        ) : (
          bankEntries.map(entry => (
            <div key={entry.id} className="history-card">
              <div className="history-header">
                <span className="history-date">
                  {new Date(entry.timestamp).toLocaleDateString()}
                </span>
                <span className={`history-distress ${entry.distress}`}>
                  {entry.distress}
                </span>
              </div>
              <div className="history-cant">
                <strong>Can't:</strong> {entry.cant}
              </div>
              <div className="history-can-dos">
                <strong>Can Do ({entry.canDos.length}):</strong>
                <ul>
                  {entry.canDos.map((cd, i) => (
                    <li key={i}>{cd}</li>
                  ))}
                </ul>
              </div>
              <div className="history-ratio">
                Achieved {entry.achievedRatio}:{1} ratio (target: {entry.requiredRatio}:1)
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
  
  const renderBankRouter = () => {
    switch (bankView) {
      case 'add-cant': return renderBankAddCant();
      case 'complete': return renderBankComplete();
      case 'history': return renderBankHistory();
      default: return renderBank();
    }
  };

  // ============================================
  // BEST FRIEND MODULE
  // ============================================
  
  const renderBestFriend = () => (
    <div className="bf-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('home')}>
          <ArrowLeft size={20} />
        </button>
        <h1>Best Friend</h1>
      </div>
      
      <div className="bf-concept">
        <div className="bf-principle">
          <h3>The Principle</h3>
          <p>You can feel <strong>unlimited feelings</strong>.</p>
          <p>But there's only <strong>one action</strong>:</p>
          <p className="bf-question">Be your own best friend.</p>
        </div>
      </div>
      
      <div className="bf-menu">
        <button 
          className="bf-menu-btn primary"
          onClick={() => {
            resetBfForm();
            setView('bf-flow');
          }}
        >
          <Heart size={24} />
          <span>Do a Best Friend Check</span>
          <p className="btn-hint">Use before making a decision</p>
        </button>
        
        <button 
          className="bf-menu-btn"
          onClick={() => setView('bf-history')}
        >
          <Clock size={24} />
          <span>Past Checks</span>
          {bfChecks.length > 0 && <span className="count">{bfChecks.length}</span>}
        </button>
      </div>
    </div>
  );
  
  const renderBfFlow = () => {
    const steps = [
      {
        title: "What's the situation?",
        content: (
          <div className="bf-input-section">
            <p className="instruction">What's happening right now that's prompting a decision?</p>
            <textarea
              placeholder="I got a text from my ex asking to meet up..."
              value={bfSituation}
              onChange={(e) => setBfSituation(e.target.value)}
              rows={3}
            />
          </div>
        ),
        canContinue: bfSituation.trim().length > 5
      },
      {
        title: "What are you feeling?",
        content: (
          <div className="bf-input-section">
            <p className="instruction">Name the feelings present right now. All of them are valid.</p>
            <textarea
              placeholder="Excited, anxious, hopeful, scared, confused..."
              value={bfFeeling}
              onChange={(e) => setBfFeeling(e.target.value)}
              rows={3}
            />
            <p className="insight">Remember: You can feel ALL of these. Feelings don't need to be resolved.</p>
          </div>
        ),
        canContinue: bfFeeling.trim().length > 2
      },
      {
        title: "What action are you considering?",
        content: (
          <div className="bf-input-section">
            <p className="instruction">What were you about to do or deciding whether to do?</p>
            <textarea
              placeholder="Say yes and meet up with them tomorrow..."
              value={bfAction}
              onChange={(e) => setBfAction(e.target.value)}
              rows={3}
            />
          </div>
        ),
        canContinue: bfAction.trim().length > 5
      },
      {
        title: "The Best Friend Question",
        content: (
          <div className="bf-question-section">
            <div className="bf-big-question">
              <p>Ask yourself:</p>
              <h3>"Am I being my own best friend by this action?"</h3>
            </div>
            <div className="bf-action-display">
              <p><strong>The action:</strong> {bfAction}</p>
            </div>
            <div className="bf-choice">
              <button 
                className={`bf-choice-btn yes ${bfHasGap === false ? 'selected' : ''}`}
                onClick={() => setBfHasGap(false)}
              >
                <Check size={24} />
                <span>Yes — this is what a best friend would do</span>
              </button>
              <button 
                className={`bf-choice-btn no ${bfHasGap === true ? 'selected' : ''}`}
                onClick={() => setBfHasGap(true)}
              >
                <X size={24} />
                <span>No — a best friend would do something different</span>
              </button>
            </div>
          </div>
        ),
        canContinue: bfHasGap !== null
      },
      ...(bfHasGap ? [
        {
          title: "What would a best friend do?",
          content: (
            <div className="bf-input-section">
              <p className="instruction">If your best friend were in this exact situation, what would you advise them?</p>
              <textarea
                placeholder="Wait a few days before responding, talk to someone first..."
                value={bfBestAction}
                onChange={(e) => setBfBestAction(e.target.value)}
                rows={3}
              />
            </div>
          ),
          canContinue: bfBestAction.trim().length > 5
        },
        {
          title: "Commit to Yourself",
          content: (
            <div className="bf-commit-section">
              <p className="instruction">You've identified what a best friend would do:</p>
              <div className="bf-best-action-display">
                <p>{bfBestAction}</p>
              </div>
              <div className="bf-commit-check">
                <label>
                  <input 
                    type="checkbox" 
                    checked={bfCommit}
                    onChange={(e) => setBfCommit(e.target.checked)}
                  />
                  <span>I commit to being my own best friend by doing this</span>
                </label>
              </div>
            </div>
          ),
          canContinue: bfCommit
        }
      ] : [])
    ];
    
    const saveBfCheck = () => {
      const check = {
        id: Date.now(),
        situation: bfSituation,
        feelings: bfFeeling,
        originalAction: bfAction,
        hadGap: bfHasGap,
        bestFriendAction: bfHasGap ? bfBestAction : null,
        committed: bfHasGap ? bfCommit : true,
        timestamp: new Date().toISOString()
      };
      setBfChecks([check, ...bfChecks]);
      
      // Also add as bank deposit
      const bankEntry = {
        id: Date.now() + 1,
        cant: `Situation: ${bfSituation}`,
        distress: 'mild',
        canDos: [`I checked in with myself`, `I chose to be my own best friend`],
        requiredRatio: 2,
        achievedRatio: 2,
        timestamp: new Date().toISOString(),
        source: 'best-friend-check'
      };
      setBankEntries([bankEntry, ...bankEntries]);
      
      reportToRebuild(rebuildContext, 'bestfriend_check', {
        check_id: check.id,
        had_gap: check.hadGap,
        committed: check.committed
      });
      
      setView('bf-complete');
    };
    
    const isLastStep = bfStep === steps.length - 1;
    
    return (
      <div className="bf-flow-view">
        <div className="view-header">
          <button className="back-btn" onClick={() => {
            if (bfStep > 0) {
              setBfStep(bfStep - 1);
              if (bfStep === 4 && !bfHasGap) {
                // Skip back properly if no gap
              }
            } else {
              resetBfForm();
              setView('bestfriend');
            }
          }}>
            <ArrowLeft size={20} />
          </button>
          <h1>Best Friend Check</h1>
        </div>
        
        <div className="step-progress">
          {steps.map((_, i) => (
            <div 
              key={i} 
              className={`step-dot ${i === bfStep ? 'active' : ''} ${i < bfStep ? 'done' : ''}`}
            />
          ))}
        </div>
        
        <div className="bf-step-card">
          <h2>{steps[bfStep].title}</h2>
          {steps[bfStep].content}
        </div>
        
        <div className="step-nav">
          {isLastStep ? (
            <button 
              className="nav-btn primary" 
              onClick={saveBfCheck}
              disabled={!steps[bfStep].canContinue}
            >
              <Check size={18} /> Complete Check
            </button>
          ) : (
            <button 
              className="nav-btn primary" 
              onClick={() => {
                if (bfStep === 3 && bfHasGap === false) {
                  // No gap, skip to save
                  saveBfCheck();
                } else {
                  setBfStep(bfStep + 1);
                }
              }}
              disabled={!steps[bfStep].canContinue}
            >
              Continue <ChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
    );
  };
  
  const renderBfComplete = () => (
    <div className="bf-complete-view">
      <div className="success-card">
        <div className="success-icon">
          <Heart size={48} />
        </div>
        <h2>Best Friend Check Complete</h2>
        {bfHasGap ? (
          <p>You identified a gap and committed to what a best friend would do.</p>
        ) : (
          <p>You confirmed you're already being your own best friend.</p>
        )}
        <div className="deposit-note">
          <TrendingUp size={18} />
          <p>Deposit added to your Validation Bank!</p>
        </div>
        <button className="nav-btn primary" onClick={() => { 
          resetBfForm(); 
          setView('home'); 
        }}>
          <Home size={18} /> Done
        </button>
      </div>
    </div>
  );
  
  const renderBfHistory = () => (
    <div className="bf-history-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('bestfriend')}>
          <ArrowLeft size={20} />
        </button>
        <h1>Past Checks</h1>
      </div>
      
      <div className="history-list">
        {bfChecks.length === 0 ? (
          <div className="empty-state">
            <p>No checks yet.</p>
          </div>
        ) : (
          bfChecks.map(check => (
            <div key={check.id} className="bf-history-card">
              <div className="history-header">
                <span className="history-date">
                  {new Date(check.timestamp).toLocaleDateString()}
                </span>
                <span className={`history-badge ${check.hadGap ? 'gap' : 'aligned'}`}>
                  {check.hadGap ? 'Found Gap' : 'Aligned'}
                </span>
              </div>
              <div className="history-situation">
                <strong>Situation:</strong> {check.situation}
              </div>
              <div className="history-feelings">
                <strong>Feelings:</strong> {check.feelings}
              </div>
              <div className="history-action">
                <strong>Original action:</strong> {check.originalAction}
              </div>
              {check.hadGap && (
                <div className="history-best-action">
                  <strong>Best friend action:</strong> {check.bestFriendAction}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  // ============================================
  // MAIN RENDER ROUTER
  // ============================================
  
  return (
    <div className="app-container">
      <div style={{ background: "#080c14", padding: "8px 0", borderBottom: "1px solid rgba(100,116,139,0.15)" }} className="no-print"><div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}><a href="https://navigationalmind.com" style={{ fontFamily: "monospace", fontSize: 13, color: "#e2e8f0", textDecoration: "none", letterSpacing: "0.5px", display: "inline-flex", alignItems: "center", gap: 6 }}>← navigationalmind.com</a></div></div>
      {view === 'home' && renderHome()}
      {view === 'learn' && renderLearn()}
      {view === 'articulation' && renderArticulation()}
      {view === 'art-create' && renderArtCreate()}
      {view === 'art-saved' && renderArtSaved()}
      {view === 'art-practice' && renderArtPractice()}
      {view === 'art-practice-session' && renderArtPracticeSession()}
      {view === 'bank' && renderBankRouter()}
      {view === 'bestfriend' && renderBestFriend()}
      {view === 'bf-flow' && renderBfFlow()}
      {view === 'bf-complete' && renderBfComplete()}
      {view === 'bf-history' && renderBfHistory()}
    </div>
  );
}

export default App;
